import { useEffect, useState, useCallback, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/apiClient";
import { AppLayout } from "@/components/AppLayout";
import { SessionFeed } from "@/components/SessionFeed";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Upload, X, Sparkles, Loader2 } from "lucide-react";
import { WeeklySummary } from "@/components/WeeklySummary";

const SUBJECT_PRESETS = [
  "Mathematics", "English", "Science", "Reading", "Writing",
  "Spelling", "Phonics", "History", "Geography", "11+ Prep", "Homework Support",
];

export default function TeacherDashboard() {
  const [students, setStudents] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    const [s, sess] = await Promise.all([api.get("/students"), api.get("/sessions")]);
    setStudents(s.data);
    setSessions(sess.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteSession = async (id) => {
    await api.delete(`/sessions/${id}`);
    toast.success("Session removed");
    load();
  };

  return (
    <AppLayout
      title="My Sessions"
      subtitle="Log today's work and share it instantly with parents."
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="log-session-button">
              <Plus className="h-4 w-4 mr-1.5" /> Log a Session
            </Button>
          </DialogTrigger>
          <UploadForm students={students} onDone={() => { setOpen(false); load(); }} />
        </Dialog>
      }
    >
      {students.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-6 mb-6 text-sm text-muted-foreground">
          You have no students assigned yet. Ask your administrator to assign students to you.
        </div>
      )}
      {students.length > 0 && (
        <div className="rounded-lg border border-border bg-card p-4 mb-6">
          <div className="eyebrow text-[10px] text-muted-foreground mb-2">AI Weekly Summaries</div>
          <div className="flex flex-wrap gap-2">
            {students.map((s) => (
              <WeeklySummary key={s.id} studentId={s.id} studentName={s.name} label={s.name} />
            ))}
          </div>
        </div>
      )}
      <SessionFeed sessions={sessions} onDelete={deleteSession} emptyText="You haven't logged any sessions yet." />
    </AppLayout>
  );
}

function UploadForm({ students, onDone }) {
  const [studentId, setStudentId] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [subject, setSubject] = useState("");
  const [duration, setDuration] = useState(60);
  const [objectives, setObjectives] = useState("");
  const [note, setNote] = useState("");
  const [photos, setPhotos] = useState([]); // {path, previewName}
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleFiles = async (files) => {
    setUploading(true);
    for (const file of Array.from(files)) {
      const fd = new FormData();
      fd.append("file", file);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, { path: data.path, name: file.name }]);
      } catch (e) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
  };

  const removePhoto = (path) => setPhotos((p) => p.filter((x) => x.path !== path));

  const [aiBusy, setAiBusy] = useState(null);

  const polishNote = async () => {
    if (!note.trim()) { toast.error("Write a note first"); return; }
    setAiBusy("polish");
    try {
      const sName = students.find((s) => s.id === studentId)?.name || "";
      const { data } = await api.post("/ai/polish-note", { note, subject, student_name: sName });
      setNote(data.result);
      toast.success("Note polished for the parent");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setAiBusy(null);
  };

  const suggestHomework = async () => {
    if (!note.trim()) { toast.error("Write a note first"); return; }
    setAiBusy("hw");
    try {
      const { data } = await api.post("/ai/suggest-homework", { note, subject });
      setObjectives((o) => (o ? o + " · " : "") + data.result.replace(/\n+/g, " · "));
      toast.success("Homework suggested");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setAiBusy(null);
  };

  const submit = async () => {
    setSaving(true);
    try {
      await api.post("/sessions", {
        student_id: studentId,
        date, subject, duration_minutes: Number(duration),
        objectives, note,
        photo_paths: photos.map((p) => p.path),
      });
      toast.success("Session log saved & sent to parent");
      onDone();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  return (
    <DialogContent className="max-h-[92vh] overflow-y-auto" data-testid="upload-session-form">
      <DialogHeader>
        <DialogTitle className="font-brand font-light text-2xl">Log a Session</DialogTitle>
        <DialogDescription>Record today's work and share it instantly with the parent.</DialogDescription>
      </DialogHeader>
      <div className="space-y-4 py-2">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2 col-span-2">
            <Label>Student</Label>
            <Select value={studentId} onValueChange={setStudentId}>
              <SelectTrigger data-testid="student-select-dropdown"><SelectValue placeholder="Select student" /></SelectTrigger>
              <SelectContent>
                {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.grade ? ` · ${s.grade}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Date</Label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="session-date-input" />
          </div>
          <div className="space-y-2">
            <Label>Duration (mins)</Label>
            <Input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} data-testid="session-duration-input" />
          </div>
          <div className="space-y-2 col-span-2">
            <Label>Subject</Label>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {SUBJECT_PRESETS.map((preset) => (
                <button
                  key={preset}
                  type="button"
                  data-testid={`subject-preset-${preset.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => setSubject(preset)}
                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${subject === preset ? "bg-foreground text-background border-foreground" : "border-border hover:bg-accent"}`}
                >
                  {preset}
                </button>
              ))}
            </div>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Pick a subject above or type your own" data-testid="session-subject-input" />
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Learning objectives / homework (optional)</Label>
            <button
              type="button"
              onClick={suggestHomework}
              disabled={aiBusy === "hw"}
              data-testid="suggest-homework-button"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              {aiBusy === "hw" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Suggest homework
            </button>
          </div>
          <Input value={objectives} onChange={(e) => setObjectives(e.target.value)} placeholder="What did you focus on?" data-testid="session-objectives-input" />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label>Session notes</Label>
            <button
              type="button"
              onClick={polishNote}
              disabled={aiBusy === "polish"}
              data-testid="polish-note-button"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors disabled:opacity-50"
            >
              {aiBusy === "polish" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />}
              Polish with AI
            </button>
          </div>
          <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Jot down rough notes, then tap 'Polish with AI'…" data-testid="session-note-input" />
        </div>

        <div className="space-y-2">
          <Label>Photos of work</Label>
          <div
            onClick={() => fileRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
            className="cursor-pointer rounded-lg border border-dashed border-border p-6 text-center hover:bg-accent transition-colors"
            data-testid="photo-upload-dropzone"
          >
            <Upload className="h-5 w-5 mx-auto text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">{uploading ? "Uploading…" : "Click or drag photos here"}</p>
            <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} data-testid="photo-file-input" />
          </div>
          {photos.length > 0 && (
            <div className="grid grid-cols-4 gap-2 mt-2">
              {photos.map((p) => (
                <div key={p.path} className="relative aspect-square rounded-md overflow-hidden border border-border group">
                  <AuthImage path={p.path} alt={p.name} className="h-full w-full object-cover" />
                  <button onClick={() => removePhoto(p.path)} data-testid="remove-photo-button" className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <DialogFooter>
        <Button
          onClick={submit}
          disabled={saving || uploading || !studentId || !subject || !note}
          className="rounded-full"
          data-testid="submit-session-button"
        >
          {saving ? "Saving…" : "Save & Share with Parent"}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
